import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, inject } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ActivatedClientService, AuthService, CLIENT_UNSAVED_CHANGES, configureAuth, Permission } from '@vdms-hq/auth';
import { ClientAdminUnsavedChangesService } from '@vdms-hq/client-admin-core';
import { configureApiContract } from '@vdms-hq/api-contract';
import { configureErrorHandling, configureSentry } from '@vdms-hq/error-handling';
import { configureLoginPage } from '@vdms-hq/login';
import { configureMetadataRecognition } from '@vdms-hq/metadata-recognition';
import { configureOrders, configureOrdersLp } from '@vdms-hq/orders';
import { configureStorage } from '@vdms-hq/storage';
import { configureTheming } from '@vdms-hq/theming';
import { configureToasts } from '@vdms-hq/toast';
import { configureTranscription, TranscriptionActionAdapter } from '@vdms-hq/transcription';
import { configureTranslation, configureUI } from '@vdms-hq/ui';
import { DefaultValueFormatterService } from '@vdms-hq/value-formatter';
import { provideUserDateFormat } from '@vdms-hq/user-date-format';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { StorageHandlerService } from './logic/services/storage-handler.service';
import { configureAnalytics } from '@vdms-hq/analytics';
import { configureSearchMainPage } from '@vdms-hq/asset-search-main-page';
import { configureCredentials } from '@vdms-hq/credentials';
import { configureUserSettings, TzDatePipe, USER_SETTINGS_ROUTER_BASE } from '@vdms-hq/user-settings';
import { vidaOnboardingProviders } from './onboarding.providers';
import { configureClientsPage } from '@vdms-hq/clients';
import { configureDynamicForm } from '@vdms-hq/dynamic-form';
import { configureDynamicFilters } from '@vdms-hq/dynamic-filters';
import { clientMismatchConfig, clientSwitcherConfig } from '@vdms-hq/activated-client-switcher';
import { configureUsers } from '@vdms-hq/users-core';
import { SupplierActions } from '@vdms-hq/suppliers';
import { configureMetadataList } from '@vdms-hq/metadata-list';
import {
  ActiveAssetService,
  AssetQuickPreviewFormService,
  configureAssetDetailsActive,
  QcExportService,
} from '@vdms-hq/asset-details';
import { ASSET_QUICK_PREVIEW, AssetQuickPreviewOpener } from '@vdms-hq/asset-search';
import { QuarantineActionAdapter, QuarantineActionsService } from '@vdms-hq/quarantine';
import { AdBreaksActionAdapter } from '@vdms-hq/ad-breaks';
import { configureFields } from '@vdms-hq/fields';
import { MandatoryFieldsDatasource } from '@vdms-hq/mandatory-fields';
import { configureAssetKits } from '@vdms-hq/asset-kits';
import { configureLicensePackages, LicensePackagesActionsService } from '@vdms-hq/license-packages';
import { configurePlayer } from '@vdms-hq/player';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { configureAssetResults, configureProjectsActions } from '@vdms-hq/asset-results';
import { ProjectsActionsService } from '@vdms-hq/projects';
import { SharedModule } from '@vdms-hq/shared';
import { firebaseContractConfig } from '@vdms-hq/firebase-contract';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { configureRevisions } from '@vdms-hq/revisions';

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom([HttpClientModule, SharedModule]),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    firebaseContractConfig({
      firestoreDomain: environment.firebaseEmulators?.firestoreDomain,
    }),
    configureAuth({
      microsoftTenantId: environment.microsoftTenantId,
      cookieDomain: environment.cookieDomain,
      authDomain: environment.firebaseEmulators?.authDomain,
      functionsDomain: environment.firebaseEmulators?.functionsDomain,
      appPermission: Permission.GENERAL_LOGIN_TO_VIDA,
      authRouting: {
        forAuthorized: '/home',
        forUnauthorized: '/login',
      },
      clientRouting: {
        insufficientPermissionsRoute: '/home',
        clientSwitchRoute: '/home',
      },
      resolveClientByHostname: false,
    }),
    configureUI({
      params: {
        appVersion: environment.version,
      },
      timezoneDatePipe: TzDatePipe,
    }),
    DefaultValueFormatterService,
    provideUserDateFormat(),
    ...configureAssetDetailsActive({
      transcriptionProvider: TranscriptionActionAdapter,
      quarantineProvider: QuarantineActionAdapter,
      qcExportProvider: QcExportService,
      adBreaksProvider: AdBreaksActionAdapter,
    }),
    configureAssetResults({
      quarantineActionsProvider: QuarantineActionsService,
    }),
    provideAnimations(),
    provideRouter(
      routes,
      withInMemoryScrolling(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
    ),
    configureToasts({
      durationInSeconds: 5,
      position: 'top-right',
      isCloseButton: true,
    }),
    configureApiContract({
      clientIdProvider: ActivatedClientService,
      authProvider: AuthService,
      appType: 'vida',
      websocketUrl: environment.websocketUrl,
      dolbyRaspApiUrl: environment.dolbyRaspApiUrl,
      dolbyRaspApiUrlV2: environment.dolbyRaspApiUrlV2,
      production: environment.production,
      apiUrl: environment.apiUrl,
      cookieDomain: environment.cookieDomain,
      releaseNotes: {
        dark: environment.releaseNotes.dark,
        light: environment.releaseNotes.light,
      },
      interceptors: {
        bearer: true,
        client: true,
        errorHandling: true,
        cloudfrontCookie: {
          notRequiredForPrefixes: [
            '/order/download',
            '/order/aspera-download',
            '/order/aspera-download/**',
            'request-new-account',
            'breaking-news/guest',
            'group/origin**',
            'user/auth/me',
          ],
        },
        ccSalesforceToken: true,
        cacheControl: true,
        headerEncoding: true,
        throttling: {
          period: 10000,
          limit: 25,
          limitFor: [
            {
              endpoint: 'asset/aspera',
              limit: 50,
            },
          ],
        },
      },
    }),
    configureTranslation([
      { prefix: './assets/common/i18n/', suffix: `.json?hash=${Date.now()}` },
      { prefix: './assets/i18n/', suffix: `.json?hash=${Date.now()}` },
    ]),
    configureStorage(
      {
        cdn: environment.cdn,
      },
      {
        remoteHost: environment.asperaRemoteHost,
        remoteUser: environment.asperaRemoteUser,
        destinationRoot: environment.asperaDestinationRoot,
        debug: !environment.production,
      },
    ),
    configureDynamicFilters({ valueFormatterService: DefaultValueFormatterService }),
    configureDynamicForm({ valueFormatterService: DefaultValueFormatterService }),
    configureLoginPage({
      app: 'vida',
      useBrandLogin: true,
      useCustomPasswordlessEmail: true,
      providers: {
        microsoft: environment.microsoftEnabled,
        google: true,
        okta: environment.oktaEnabled,
        oktaProviders: environment.octa.amazonProvider
          ? [{ name: 'Amazon', provider: environment.octa.amazonProvider, icon: 'amazon.svg' }]
          : [],
      },
    }),
    configureMetadataRecognition(),
    configureTranscription({
      activeAssetProvider: ActiveAssetService,
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: (storageHandlerService: StorageHandlerService) => () => storageHandlerService.registerListeners(),
      multi: true,
      deps: [StorageHandlerService],
    },
    clientSwitcherConfig(),
    // Client switches outside the admin panel respect its unsaved edits.
    { provide: CLIENT_UNSAVED_CHANGES, useExisting: ClientAdminUnsavedChangesService },
    {
      provide: ASSET_QUICK_PREVIEW,
      useFactory: (): AssetQuickPreviewOpener => inject(AssetQuickPreviewFormService),
    },
    // Multi-tab client isolation: block writes + warn when another tab changes client.
    clientMismatchConfig(),
    configureClientsPage({
      firebaseProjectId: environment.firebaseConfig.projectId,
      appUrls: [
        {
          type: 'vida',
          url: environment.frontpageUrls.vida,
        },
        {
          type: 'launchpad',
          url: environment.frontpageUrls.launchpad,
        },
        {
          type: 'mediafactory',
          url: environment.frontpageUrls.mediafactory,
        },
      ],
    }),
    configureCredentials({ oAuthGoogleClientId: environment.googleOAuthClientId }),
    configureTheming(),
    configureOrders({ cdn: environment.cdn }),
    configureOrdersLp({ licensedPackagesActionServiceProvider: LicensePackagesActionsService }),
    configureProjectsActions({ projectsActionServiceProvider: ProjectsActionsService }),
    configureSentry({
      printInConsole: true,
    }),
    configureAnalytics({
      appName: 'vida',
      debug: false,
    }),
    configureSearchMainPage(),
    configureUserSettings({
      reauthReturnUrl: `${USER_SETTINGS_ROUTER_BASE.ROOT}/${USER_SETTINGS_ROUTER_BASE.PROFILE}`,
    }),
    // The product tour has to leave the hub to highlight real UI; without this bypass the guard bounces it back.
    ...vidaOnboardingProviders(),
    configureUsers({
      createSupplier: SupplierActions,
    }),
    configureMetadataList({
      activeAssetProvider: ActiveAssetService,
    }),
    configureRevisions({
      activeAssetProvider: ActiveAssetService,
    }),
    configureAssetKits({
      activeAssetProvider: ActiveAssetService,
    }),
    configureLicensePackages({
      activeAssetProvider: ActiveAssetService,
    }),
    configureFields({
      mandatoryFieldsDataSourceProvider: MandatoryFieldsDatasource,
    }),
    configurePlayer({
      omakase: {
        // add cookies to all cdn requests that are not m3u8 (hls.js support cookies by separate config)
        urlWithCredentials: new RegExp('^' + environment.cdn + '(?!.*(m3u8))'),
      },
    }),
    configureErrorHandling({
      apiUrl: environment.apiUrl,
      mfaSetupRoute: '/onboarding',
    }),
  ],
};
